import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock } from "lucide-react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import FormField from "../../components/forms/FormField/FormField.jsx";
import Select from "../../components/forms/Select/Select.jsx";
import PageHeader from "../../components/marketing/PageHeader.jsx";
import SeoHead from "../../seo/SeoHead.jsx";
import ShareRow from "../../components/share/ShareRow.jsx";
import { ROUTE_META } from "../../seo/siteMeta.js";
import { breadcrumbSchema, faqSchema } from "../../seo/schema.js";
import { dueDateRows, dueDateFor, nextDueDate, currentPeriod } from "../../data/taxPeriods.js";

/**
 * Public Form 2290 due-date calculator. Every date is a row of the IRS "When
 * To File" table transcribed in taxPeriods.js (weekend and holiday roll-forward
 * already applied by the IRS) - this page looks dates up, it never computes
 * one. The default selection is the month whose deadline comes next.
 */

const meta = ROUTE_META["/form-2290-due-date-calculator"];

const FAQS = [
  {
    q: "When is Form 2290 due?",
    a: "By the last day of the month after the month the vehicle was first used on a public highway during the July-to-June period. Trucks in service in July are due by August 31; a truck first used in October is due by November 30. If the date falls on a weekend or legal holiday, the deadline moves to the next business day.",
  },
  {
    q: "Is the Form 2290 deadline tied to my registration renewal?",
    a: "No. The IRS deadline depends only on the month of first use, regardless of when the vehicle's state registration is due.",
  },
  {
    q: "Can I get an extension to file Form 2290?",
    a: "You can ask the IRS in writing, before the due date, explaining the cause of the delay. An extension of time to file doesn't extend the time to pay the tax.",
  },
  {
    q: "Do I file a new Form 2290 for trucks added later in the year?",
    a: "Yes. Vehicles first used in a different month go on a separate return, due by the end of the month after that month.",
  },
];

const DueDateCalculator = () => {
  const rows = dueDateRows();
  const next = nextDueDate();
  const [month, setMonth] = useState(next ? next.month : rows[0].month);
  const picked = dueDateFor(month);

  return (
    <div className="max-w-[1180px] mx-auto px-[22px] py-[46px] pb-[70px]">
      <SeoHead
        title={meta.title}
        description={meta.description}
        path="/form-2290-due-date-calculator"
        keywords={meta.keywords}
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Form 2290 due date calculator", path: "/form-2290-due-date-calculator" },
          ]),
          faqSchema(FAQS),
        ]}
      />
      <PageHeader eyebrow={`Tax period ${currentPeriod().label}`} title="Form 2290 Due Date Calculator">
        Your Form 2290 deadline depends on one thing: the month your truck was first used on a public highway this
        period. Pick the month to see the date to file and pay — taken straight from the IRS filing chart.
      </PageHeader>

      <div className="grid gap-6 mt-8 lg:grid-cols-[1fr_1fr] items-start">
        <Panel padded>
          <FormField label="When was the vehicle first used on a public highway?">
            <Select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month first used">
              {rows.map((r) => (
                <option key={r.month} value={r.month}>
                  {r.monthLabel}
                </option>
              ))}
            </Select>
          </FormField>
          <div className="flex gap-3 items-start mt-2">
            <CalendarClock size={22} className="flex-none text-[var(--accent)] mt-1" />
            <div>
              <div className="text-sm text-[var(--muted)]">File Form 2290 and pay by</div>
              <div className="font-disp text-[30px] leading-tight">{picked.dueLabel}</div>
              <div className="text-sm text-[var(--muted)] mt-1">
                {picked.monthsRemaining === 12
                  ? "Taxed for the full 12-month period."
                  : `Taxed for ${picked.monthsRemaining} months — the rest of the period.`}
              </div>
            </div>
          </div>
          <div className="grid gap-2.5 mt-6">
            <Link to="/login?tab=up">
              <Button block>
                Create your free account <ArrowRight size={16} />
              </Button>
            </Link>
            <Link to="/wizard/1" className="text-center text-sm font-semibold text-[var(--accent)]">
              Already have an account? Start filing →
            </Link>
          </div>
        </Panel>

        <Panel className="overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {["First used in", "File and pay by"].map((h) => (
                  <th key={h} className="text-left p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.month} className={r.month === month ? "bg-[var(--accent-soft)]" : ""}>
                  <td className="p-3 border-b border-[var(--line)] text-sm">{r.monthLabel}</td>
                  <td className="p-3 border-b border-[var(--line)] text-sm font-semibold">
                    {r.dueLabel}
                    {next && r.month === next.month && <span className="ml-2 text-xs text-[var(--amber)]">next deadline</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>

      <section className="mt-12 max-w-[78ch]">
        <h2 className="font-disp text-[25px]">Know the deadline — and the tax</h2>
        <p className="mt-3 leading-[1.7]">
          The month of first use also sets how much you pay: a truck first used after July is taxed only for the
          months left in the period. Price it with the{" "}
          <Link to="/form-2290-tax-calculator" className="text-[var(--accent)]">Form 2290 tax calculator</Link>, or
          read the <Link to="/blog/form-2290-due-date-2026-2027" className="text-[var(--accent)]">full guide to Form 2290 due dates</Link>.
          Missed one? See <Link to="/blog/form-2290-late-filing-penalties" className="text-[var(--accent)]">what happens after a late Form 2290</Link>.
        </p>
      </section>

      <section className="mt-10 max-w-[78ch]">
        <h2 className="font-disp text-[25px] mb-4">Frequently asked questions</h2>
        <div className="grid gap-3">
          {FAQS.map((f) => (
            <Panel key={f.q} padded>
              <b className="font-disp text-[16px]">{f.q}</b>
              <p className="text-[var(--muted)] text-sm mt-1.5 leading-[1.65]">{f.a}</p>
            </Panel>
          ))}
        </div>
      </section>

      <ShareRow path="/form-2290-due-date-calculator/" title="When is your Form 2290 due? Pick the month your truck was first used:" />

      <p className="text-[var(--muted)] text-xs mt-11 pt-5 border-t border-[var(--line)] max-w-[70ch]">
        {currentPeriod().source}. General information, not tax advice for your situation.
      </p>
    </div>
  );
};

export default DueDateCalculator;
